/* Chi tiết một Product: đề bài + hướng dẫn, hoặc form nộp bài (lưu tạm / chấm thử / chấm thật). */
import { useEffect, useState, type ChangeEvent, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Spinner } from "@/components/ui/spinner"
import { Icon } from "@/components/icon"
import { StatusNote } from "@/components/page"
import { post } from "@/lib/api"
import { localSubmissionUrlMessage, prepareEvidenceImage } from "@/lib/evidence-image"
import { cn } from "@/lib/utils"
import { ProductFocus, ProductGuide } from "./guide"
import { QUEUED, StatusBadge, TrialRubric } from "./shared"

type Form = { product_name: string; artifact_url: string; audience: string; audience_need: string; solution: string; user_feedback: string }
const EMPTY: Form = { product_name: "", artifact_url: "", audience: "", audience_need: "", solution: "", user_feedback: "" }
const formFrom = (s: any): Form => ({
  product_name: s?.product_title || "",
  artifact_url: s?.artifact_url || "",
  audience: s?.audience || "",
  audience_need: s?.audience_need || "",
  solution: s?.solution || "",
  user_feedback: s?.user_feedback || "",
})
const kb = (bytes: any) => Math.ceil(Number(bytes || 0) / 1024)

function FormField({ id, label, hint, children }: { id: string; label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="font-semibold text-ink">
        {label}
      </label>
      {children}
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function ProductDetail({ product, guidance, slug, reload, index, total }: { product: any; guidance: any; slug: string; reload: () => void; index: number; total: number }) {
  const submissions: any[] = product.submissions || []
  const latest = submissions[0]
  const unfinished = submissions.find((s) => s.status !== "final_scored")
  const completion: string[] = Array.isArray(product.brief?.completion) ? product.brief.completion : []
  const [editing, setEditing] = useState(false)
  const [submissionId, setSubmissionId] = useState("")
  const [form, setForm] = useState<Form>(EMPTY)
  const [checks, setChecks] = useState<string[]>([])
  const [busy, setBusy] = useState("")
  const [message, setMessage] = useState<{ text: string; tone: "info" | "success" | "error" } | null>(null)
  const [evidenceImage, setEvidenceImage] = useState<any>(null)
  const [imageMessage, setImageMessage] = useState("")
  const checksFrom = (s: any) => completion.map((_, i) => String(s?.completion_checks?.[i]?.evidence || ""))

  const openEditor = () => {
    const source = unfinished || null
    setSubmissionId(source?.id || "")
    setForm(formFrom(source))
    setChecks(checksFrom(source))
    setEvidenceImage(null)
    setImageMessage(source?.evidence_image ? "Đã có 1 ảnh bằng chứng đang lưu. Upload ảnh mới nếu muốn thay thế." : "")
    setMessage(null)
    setEditing(true)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  // Khi bài đã vào hàng chấm / được chấm, form hiển thị đúng nội dung đã gửi.
  useEffect(() => {
    if (!submissionId) return
    const fresh = submissions.find((s) => s.id === submissionId)
    if (fresh && ["trial_queued", "trial_scored", "final_queued", "final_scored", "failed"].includes(fresh.status)) {
      setForm(formFrom(fresh))
      setChecks(checksFrom(fresh))
    }
  }, [product, submissionId])

  const current = submissions.find((s) => s.id === submissionId)
  const trial = current?.assessments?.find((a: any) => a.assessment_kind === "trial" && a.status === "completed" && a.input_hash === current.content_hash)
  const canFinal = Boolean(trial) && current?.status === "trial_scored"
  const currentEvidence = evidenceImage || current?.evidence_image || null

  const uploadEvidence = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setImageMessage("Đang xử lý ảnh…")
    try {
      const prepared = await prepareEvidenceImage(file)
      setEvidenceImage(prepared)
      setImageMessage(`Đã chọn 1 ảnh bằng chứng · ${kb(prepared.byte_size)}KB`)
    } catch (e: any) {
      setEvidenceImage(null)
      setImageMessage(e.message)
    } finally {
      event.target.value = ""
    }
  }

  const submit = async (action: "draft" | "trial" | "final") => {
    setMessage(null)
    const urlIssue = localSubmissionUrlMessage(form.artifact_url)
    if (urlIssue) return setMessage({ text: urlIssue, tone: "error" })
    if (action !== "draft" && completion.length > 0) {
      const missing = completion.filter((_, i) => (checks[i] || "").trim().length < 10).length
      if (missing > 0)
        return setMessage({ text: `Con cần nêu bằng chứng cụ thể cho ${missing} điều kiện hoàn thành còn thiếu trước khi chấm điểm.`, tone: "error" })
    }
    setBusy(action)
    try {
      const result = await post(`/api/classes/${encodeURIComponent(slug)}/products/${encodeURIComponent(product.id)}/submissions`, {
        id: submissionId || undefined,
        action,
        ...form,
        completion_checks: completion.map((item, i) => ({ item, evidence: (checks[i] || "").trim() })),
        evidence_image: evidenceImage || undefined,
      })
      setSubmissionId(result.id)
      setEvidenceImage(null)
      setImageMessage(result.evidence_image ? `Đã lưu 1 ảnh bằng chứng · ${kb(result.evidence_image.byte_size)}KB` : "")
      setMessage({
        text: action === "draft" ? "Đã lưu tạm." : action === "trial" ? "Simba đang chấm thử phiên bản này." : "Simba đang chấm thật phiên bản này.",
        tone: "success",
      })
      reload()
    } catch (e: any) {
      setMessage({ text: e.message, tone: "error" })
    } finally {
      setBusy("")
    }
  }

  const set = (key: keyof Form) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const suggestions = (guidance?.product_title_suggestions || ["Simba", "Lion", "Tiger"]).slice(0, 6).join(", ")

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <p className="text-sm font-bold tracking-[0.12em] text-orange-strong uppercase">
          Product {index}/{total}
        </p>
        <h1 className="font-heading text-2xl leading-tight font-bold text-ink sm:text-3xl">{latest?.product_title || product.name}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {latest?.product_title && <span className="text-base text-muted-foreground">{product.name}</span>}
          <StatusBadge status={latest?.status} />
          {latest && <span className="text-sm text-muted-foreground">Phiên bản {latest.version}</span>}
        </div>
      </header>

      {!editing ? (
        <>
          <ProductFocus brief={product.brief} actionLabel={unfinished ? "Tiếp tục bài đang làm" : "Bắt đầu nộp bài"} onAction={openEditor} />
          <ProductGuide brief={product.brief} submissions={submissions} rubric={product.rubric} exemplars={Array.isArray(product.exemplars) ? product.exemplars : []} slug={slug} />
        </>
      ) : (
        <>
          <Button variant="ghost" size="sm" className="self-start" onClick={() => setEditing(false)}>
            <Icon name="arrow-left" className="size-4" /> Xem lại đề bài
          </Button>
          <Card>
            <CardContent className="flex flex-col gap-6">
              <FormField id="p-name" label="Tên sản phẩm (Product Name)" hint={guidance?.product_name || `Đặt một cái tên cho sản phẩm của con. Gợi ý: ${suggestions}`}>
                <Input id="p-name" maxLength={80} value={form.product_name} placeholder="Ví dụ: Simba, Lion, Tiger…" onChange={set("product_name")} />
              </FormField>
              <FormField id="p-url" label="URL của bài tập" hint={guidance?.artifact_url}>
                <Input id="p-url" type="url" inputMode="url" value={form.artifact_url} placeholder="https://…" onChange={set("artifact_url")} />
              </FormField>
              <EvidencePicker
                preview={currentEvidence?.data_url}
                message={imageMessage || (currentEvidence ? `Đã có 1 ảnh bằng chứng · ${kb(currentEvidence.byte_size)}KB` : "")}
                hasEvidence={Boolean(currentEvidence)}
                canRemove={Boolean(evidenceImage)}
                onPick={uploadEvidence}
                onRemove={() => {
                  setEvidenceImage(null)
                  setImageMessage("")
                }}
              />
              <FormField id="p-aud" label="Bài này dành cho ai?" hint={guidance?.audience}>
                <Textarea id="p-aud" value={form.audience} onChange={set("audience")} />
              </FormField>
              <FormField id="p-need" label="Người đó có nhu cầu gì?" hint={guidance?.audience_need}>
                <Textarea id="p-need" value={form.audience_need} onChange={set("audience_need")} />
              </FormField>
              <FormField id="p-sol" label="Product của con đã giúp họ như thế nào?" hint={guidance?.solution}>
                <Textarea id="p-sol" value={form.solution} onChange={set("solution")} />
              </FormField>
              <FormField
                id="p-fb"
                label="Con đã gửi sản phẩm này cho họ trải nghiệm chưa? Họ phản hồi thế nào?"
                hint="Ghi lại phản hồi thật: lời nói, hành động, biểu cảm gương mặt của người đã trải nghiệm."
              >
                <Textarea
                  id="p-fb"
                  value={form.user_feedback}
                  onChange={set("user_feedback")}
                  placeholder="Ví dụ: Con gửi cho 2 bạn dùng thử. Một bạn nói 'dễ hiểu hơn sách', bạn kia cười và chơi lại 3 lần…"
                />
              </FormField>
            </CardContent>
          </Card>

          {completion.length > 0 && (
            <Card>
              <CardContent className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <h2 className="font-heading text-lg font-bold text-ink">Điều kiện hoàn thành</h2>
                  <p className="text-sm text-muted-foreground">
                    Với mỗi điều kiện, chỉ rõ bằng chứng cụ thể trong sản phẩm của con (link, ảnh, hoặc phần nào trên trang chứng minh điều đó). Cần điền đủ trước khi chấm điểm.
                  </p>
                </div>
                {completion.map((item, i) => {
                  const done = (checks[i] || "").trim().length >= 10
                  return (
                    <div key={i} className={cn("flex flex-col gap-2 rounded-2xl p-4 transition-colors", done ? "bg-success-soft" : "bg-sand")}>
                      <label htmlFor={`chk-${i}`} className="flex gap-3 font-semibold text-ink">
                        {done ? (
                          <Icon name="check-circle" className="mt-0.5 size-5 shrink-0 text-success" />
                        ) : (
                          <span className="grid size-6 shrink-0 place-items-center rounded-full bg-card text-sm font-bold text-muted-foreground">{i + 1}</span>
                        )}
                        {item}
                      </label>
                      <Textarea
                        id={`chk-${i}`}
                        value={checks[i] || ""}
                        placeholder="Bằng chứng cụ thể cho điều kiện này…"
                        onChange={(e) =>
                          setChecks((prev) => {
                            const next = [...prev]
                            next[i] = e.target.value
                            return next
                          })
                        }
                      />
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          )}

          <div className="flex flex-col gap-3">
            {message && <StatusNote tone={message.tone}>{message.text}</StatusNote>}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button variant="outline" disabled={!!busy} onClick={() => submit("draft")}>
                {busy === "draft" && <Spinner />} Lưu tạm
              </Button>
              <Button variant="secondary" disabled={!!busy || QUEUED.includes(current?.status)} onClick={() => submit("trial")}>
                {busy === "trial" ? <Spinner /> : <Icon name="sparkles" className="size-4" />} Chấm điểm thử
              </Button>
              <Button disabled={!!busy || !canFinal} onClick={() => submit("final")}>
                {busy === "final" ? <Spinner /> : <Icon name="trophy" className="size-4" />} Chấm điểm thật
              </Button>
            </div>
            {!canFinal && <p className="text-sm text-muted-foreground">Con cần chấm thử phiên bản hiện tại trước khi chấm thật.</p>}
          </div>

          {current?.status === "trial_queued" && (
            <div className="flex items-center gap-3 rounded-2xl bg-soft px-4 py-3">
              <Spinner className="size-5 text-orange" />
              <span className="text-ink">
                <b>Đang chấm điểm thử…</b> Simba đang chấm, con đợi một chút nhé.
              </span>
            </div>
          )}
          {current?.status === "final_queued" && (
            <div className="flex items-center gap-3 rounded-2xl bg-soft px-4 py-3">
              <Spinner className="size-5 text-orange" />
              <span className="text-ink">
                <b>Đang chấm điểm thật…</b> Kết quả sẽ hiện trong Lịch sử phiên bản.
              </span>
            </div>
          )}

          {trial && (
            <Card className="bg-sand">
              <CardContent className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-bold tracking-[0.12em] text-orange-strong uppercase">Kết quả chấm điểm thử</p>
                  {trial.normalized_score != null && <span className="font-heading text-2xl font-bold text-ink">{Math.round(trial.normalized_score * 100)} điểm</span>}
                </div>
                {trial.result?.overall_feedback && <p className="text-base text-ink">{trial.result.overall_feedback}</p>}
                {trial.result?.next_step && (
                  <p className="rounded-xl bg-card p-3 text-base text-ink">
                    <b>Bước tiếp theo:</b> {trial.result.next_step}
                  </p>
                )}
                <TrialRubric rubric={product.rubric} result={trial.result} />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

function EvidencePicker({
  preview,
  message,
  hasEvidence,
  canRemove,
  onPick,
  onRemove,
}: {
  preview?: string
  message: string
  hasEvidence: boolean
  canRemove: boolean
  onPick: (e: ChangeEvent<HTMLInputElement>) => void
  onRemove: () => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-semibold text-ink">Ảnh bằng chứng</span>
      <label className="flex cursor-pointer items-center gap-3 rounded-2xl border-[1.5px] border-dashed border-edge bg-sand px-4 py-4 transition-colors hover:border-orange focus-within:border-orange">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-card">
          <Icon name="plus" className="size-5" />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="font-semibold text-ink">{hasEvidence ? "Thay ảnh khác" : "Chọn ảnh"}</span>
          <span className="text-sm text-muted-foreground">Không bắt buộc · tối đa 1 ảnh · Simba tự nén để lưu không quá 200KB.</span>
        </span>
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onPick} className="sr-only" />
      </label>
      {(message || hasEvidence) && (
        <div className="flex items-center gap-3 rounded-2xl bg-card p-2 pr-3 ring-1 ring-line">
          {preview ? (
            <img src={preview} alt="Ảnh bằng chứng đã chọn" className="size-16 shrink-0 rounded-xl object-cover" />
          ) : (
            <span className="grid size-16 shrink-0 place-items-center rounded-xl bg-soft">
              <Icon name="file" className="size-6" />
            </span>
          )}
          <span className="min-w-0 flex-1 text-sm text-ink">{message}</span>
          {canRemove && (
            <Button variant="ghost" size="sm" onClick={onRemove}>
              Bỏ ảnh
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
